import { Box, Button, Divider, Paper, Typography } from "@mui/material";
import { setProductQuantity, setProducts } from "api/slice/productsSlice/productsSlice";
import { BlueWarnIcon } from "assets/icons";
import { getMarathonsDistancesPrice } from "api/thunk/marathons/getActions/getMarathonsDistancesPrice";
import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "shared/config/configureStore";
import { useAppSelector } from "shared/lib/hooks/hooks";
import { setMarathonDistancePrice, setSelectedDistance } from "api/slice/marathonsSlice/marathonsSlice";

const SummaryRow = ({ label, value }: { label: string; value: number | string }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", paddingBottom: "8px" }}>
    <Typography>{label}</Typography>
    <Typography>{value}</Typography>
  </Box>
);

interface ISummeryProps {
  showButton?: boolean;
  changeDistance?: boolean;
  setChangeDistance?: any;
}

const Summary: FC<ISummeryProps> = ({ showButton, changeDistance, setChangeDistance }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { marathon, isInsuranceNeeded, distanceId, distancePrice } = useAppSelector(
    (state) => ({
      marathon: state.marathons.marathonsById.response.data,
      isInsuranceNeeded: state.marathons.marathonsById.params.insurance,
      distanceId: state.marathons.marathonsById.params.distance,
      distancePrice: state.marathons.marathonsDistancesPrice.response.data,
    })
  );
  const products = useAppSelector((state) => state.products.productsById.params.products);
  const promocodes = useAppSelector((state) => state.promocodes.promocodesValidate.data);
  const registrationInfo = useAppSelector((state) => state.marathons.userRegistered.data);

  const totalPrice = products?.reduce((sum, product) => {
    return sum + product.price * product.quantity;
  }, 0);

  const selectedDistance = marathon?.distances.find((d) => Number(d.id) === Number(distanceId));
  const todayPrice = selectedDistance?.distancePrices.find((p: any) => p.isToday)?.price;

  const promocodeId =
    promocodes && typeof promocodes === "object"
      ? promocodes.id
      : undefined;

  useEffect(() => {
    if (distanceId === null) return;
    if (!marathon?.id) return;

    dispatch(getMarathonsDistancesPrice({
      marathonId: Number(marathon.id),
      distanceId: Number(distanceId),
      promocodeId: promocodeId,
      includeInsurance: isInsuranceNeeded,
    }));
  }, [distanceId, isInsuranceNeeded, marathon, dispatch, promocodeId]);


  const handleChangeDistance = () => {
    setChangeDistance(false);
    dispatch(setProducts(null));
    dispatch(setProductQuantity(null));
    dispatch(setSelectedDistance(null));
    dispatch(setMarathonDistancePrice(null));
  }

  return (
    <Paper
      sx={{
        borderRadius: "16px",
        padding: "32px",
        boxShadow: "none",
        // marginBottom: {
        //   xs: "0",
        //   md: "0",
        //   xl: "16px"
        // },
        marginBottom: "16px"
      }}>
      <Typography sx={{ fontSize: "20px", fontWeight: 600, color: "#0D0C0D", paddingBottom: "16px" }}>
        {t("myMarathon.yourOrder")}
      </Typography>

      {todayPrice && <SummaryRow label={t("registerToMarathon.registration")} value={`${todayPrice} ${t("myMarathon.som")}`} />}
      {isInsuranceNeeded && marathon && <SummaryRow label={t("registerToMarathon.insurance")} value={`${marathon.insurancePrice} ${t("myMarathon.som")}`} />}

      {products?.map((product) => (
        <SummaryRow
          key={product.productId}
          label={`${product.name}, ${product.quantity} ${t("registerToMarathon.pieces")}`}
          value={`${product.price * product.quantity} ${t("myMarathon.som")}`}
        />
      ))}

      <Divider
        sx={{
          flexGrow: 1,
          height: "1px",
          my: 2,
          backgroundColor: "#ECF2F7",
        }}
      />
      <Box sx={{ py: "8px" }}>
        {changeDistance && (
          <Box
            sx={{
              display: "flex",
              gap: "6px",
              backgroundColor: "#F5FAFF",
              padding: "8px",
              color: "#0090D1",
              borderRadius: "10px",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                minWidth: { xs: 24, sm: 24, md: 24, xl: 24 },
                height: { xs: 24, sm: 24, md: 24, xl: 24 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mt: "2px",
              }}
            >
              <BlueWarnIcon
                width={24}
                height={24}
                style={{
                  width: "100%",
                  height: "100%",
                }} />
            </Box>

            <Typography
              sx={{
                fontSize: { xs: 12, sm: 12, md: 12.5, xl: 12.5 },
                fontWeight: 600,
                lineHeight: "16px",
                wordBreak: "break-word",
              }}
            >
              {t("myMarathon.priceCalculatedWithPreviousPayment")}{" "}
              {registrationInfo?.paid}
            </Typography>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between"
        }}
      >
        <Typography sx={{ fontSize: "20px", fontWeight: 600, color: "#0D0C0D", paddingBottom: "24px" }}>
          {t("myMarathon.total2")}
        </Typography>
        <Typography>
          {((distancePrice || 0) + (totalPrice || 0))} {t("myMarathon.som")}
        </Typography>
      </Box>

      {changeDistance && (
        <Button
          variant="contained"
          onClick={handleChangeDistance}
          sx={{
            width: "100%",
            background: "#0090D1",
            borderRadius: "12px",
            padding: "8px 0",
            "&:hover": {
              backgroundColor: "#007BB3",
            },
          }}
        >
          {t("myMarathon.cancelChangeDistance")}
        </Button>
      )}

      {/* {showButton && (
        <Button
          variant="contained"
          sx={{ width: "100%", background: "#0090D1", borderRadius: "12px", padding: "8px 0" }}
        >
          {t("buttons.proceedToPayment")}
        </Button>
      )} */}
    </Paper>
  );
};

export default Summary;